/**
 * THIS CODE IS PORTED OVER TO chatmeter/flat-cats/src/services/review/ReviewResponseConfiguration.js
 * A central place for the various finicky review response configuration options
 */
angular.module('clientDashboardApp').factory('reviewResponseConfiguration', (
  $http, userService, configService, $rootScope, $q,
  providerService
) => {

  const TRUSTPILOT = "TRUSTPILOT";

  function configWithDefaults(sparse) {
    return {
      requiresProviderReviewId: false, // whether we require the review.providerReviewId field in order to respond
      canRespond: false, // whether the user / account has the required configuration for responding to this provider
      canCreateDraft: false, // whether the user has permission to create a response draft
      canRespondBulk: true, // whether the review can be responded to as part of a multi response
      canRespondBulkFalseyMessage: undefined, // A message to display over the multi select checkbox if the user cannot respond in bulk
      imitateResponsesForDemo: false, // whether the component fake a response despite canRespond for demo purposes
      usesCredentials: false, // whether this provider requires user entered credentials for responding. Usually goes hand in hand with the following 2
      createCredentials: false, // whether this provider can have credentials added. This is split from the above because YELP != YELPCA, and YELPCA just reuses the YELP credentials.
      requiresRespondableListing: false, // whether the review's listing must be associated with a credential to respond
      shouldShowForm: false, // whether the response form should be shown (even if the provider is completely unsupported). I guess we want this a teaser? seems kind of annoying to me
      isReviewBuilder: false, // whether this is a review builder provider and should be treated as less public
      unsupported: false, // whether this provider has an integration (despite the accounts configuration)
      ...sparse
    }
  }

  function toConfigForProvider(provider, user, brandConfig) {
    const p = provider && provider.id;
    let config;
    const isAccountDisabled = brandConfig.restrictions.hideReviewResponses;
    const isDemo = !brandConfig.restrictions.hideDemoReviewResponses
    const hasApprovePermission = user.permissions.includes('ApproveReviewResponse')
    const hasDraftPermission = user.permissions.includes('SubmitReviewResponse')

    if (p === 'FACEBOOK') {
      config = configWithDefaults({
        canRespond: hasApprovePermission && !isAccountDisabled,
        canCreateDraft: hasDraftPermission && !isAccountDisabled,
        imitateResponsesForDemo: isDemo,
        usesCredentials: true,
        createCredentials: true,
        requiresRespondableListing: true,
      })
    } else if (p === 'GOOGLE') {
      config = configWithDefaults({
        canRespond: hasApprovePermission && !isAccountDisabled,
        canCreateDraft: hasDraftPermission && !isAccountDisabled,
        imitateResponsesForDemo: isDemo,
        usesCredentials: true,
        createCredentials: true,
        requiresRespondableListing: true,
        requiresProviderReviewId: !isDemo
      })
    } else if (provider && provider.providerSettings.isReviewBuilder) {
      config = configWithDefaults({
        canRespond: hasApprovePermission,
        canCreateDraft: true,
        isReviewBuilder: true
      })
    } else if (p === 'APARTMENTGUIDE' || p === 'RENT') {
      config = configWithDefaults({
        canRespond: !isAccountDisabled && hasApprovePermission && !brandConfig.restrictions.hideApartmentGuideReviewResponses,
        canCreateDraft: !isAccountDisabled && hasDraftPermission && !brandConfig.restrictions.hideApartmentGuideReviewResponses,
        imitateResponsesForDemo: isDemo,
      })
    } else if (p === 'GOLFADVISOR') {
      config = configWithDefaults({
        canRespond: !isAccountDisabled && hasApprovePermission && !brandConfig.restrictions.hideGolfAdvisorReviewResponses,
        canCreateDraft: !isAccountDisabled && hasDraftPermission && !brandConfig.restrictions.hideGolfAdvisorReviewResponses,
        imitateResponsesForDemo: isDemo,
        unsupported: false
      })
    } else if (['YELP', 'YELPCA', 'YELPGB'].includes(p)) {
      const yelpEnabled = !brandConfig.restrictions.hideYelpResponding;
      config = configWithDefaults({
        canRespond: hasApprovePermission && (yelpEnabled || isDemo),
        canCreateDraft: hasDraftPermission && (yelpEnabled || isDemo),
        canRespondBulk: false, // Yelp does not allow bulk responses
        canRespondBulkFalseyMessage: "Bulk responding is not available for Yelp reviews.",
        imitateResponsesForDemo: false, //isDemo, TODO - finish yelp integration
        usesCredentials: yelpEnabled,
        createCredentials: (p === 'YELP') && yelpEnabled,
        requiresRespondableListing: yelpEnabled,
        unsupported: true // TODO - finish yelp integration
      })
    } else if (p === 'CARS') {
      config = configWithDefaults({
        canRespond: hasApprovePermission && !isAccountDisabled,
        canCreateDraft: hasDraftPermission && !isAccountDisabled,
        imitateResponsesForDemo: isDemo
      })
    } else if (p === TRUSTPILOT) {
      config = configWithDefaults({
        createCredentials: true,
        requiresProviderReviewId: true,
        requiresRespondableListing: true,
        canRespond: hasApprovePermission && !isAccountDisabled
      });
    } else {
      config = configWithDefaults({ unsupported: true })
    }

    return {
      ...config,
      provider: provider,
      shouldShowForm: ((hasApprovePermission || hasDraftPermission) && !isAccountDisabled) || config.canRespond || config.canCreateDraft
    }
  }





  const self = {

    fetchResponseConfiguration() {
      self.responseConfigurationCache = self.responseConfigurationCache || userService.cacheResultUntilLoggedOut(() => {
        return $q.all({
          user: userService.currentUser(),
          brandConfig: configService.getBrandConfig(),
          providers: providerService.validProviders('all', providerService.TYPE_REVIEW_RESPONSES)
        }).then(({
          user, brandConfig, providers
        }) => {
          self.responseConfigurationData = providers.concat([undefined]).reduce((sum, p) => {
            const config = toConfigForProvider(p, user, brandConfig);
            return {
              ...sum,
              [p && p.id || '__DEFAULT__']: config
            }
          }, {})
          return self.responseConfigurationData
        })

      });
      return self.responseConfigurationCache.get()
    },

    listReviewResponseCredentialProviders() {
      return self.fetchResponseConfiguration().then(config => {
        return Object.values(config).filter(x => {
          return x.createCredentials && !!x.provider
        }).map(x => x.provider)
      })
    },

    // sometimes you need something synchronous. Only call after the fetch has been awaited or you'll get nothing
    responseConfiguration() {
      if (self.responseConfigurationData) {
        return self.responseConfigurationData
      } else {
        self.fetchResponseConfiguration();
        return undefined
      }
    },

    forProvider(p) {
      const c = self.responseConfiguration()
      if (!c) {
        return configWithDefaults({})
      } else {
        return self.responseConfiguration()[p] || self.responseConfiguration()['__DEFAULT__']
      }
    },

    isResponseAllowed(review) {
      const config = self.forProvider(review.contentProvider);
      return config.canRespond && // user / provider has the permission
        (
          !config.requiresRespondableListing || // has credentials
            $rootScope.respondableListings.includes(review.listingId) ||
            config.imitateResponsesForDemo
        ) &&
        !(config.requiresProviderReviewId && review.providerReviewId === undefined)
    },

    isDraftResponseAllowed(review) {
      const config = self.forProvider(review.contentProvider)
      return config.canCreateDraft && // user / provider has the permission
        (
          !config.requiresRespondableListing || // has credentials
          $rootScope.respondableListings.includes(review.listingId) ||
          config.imitateResponsesForDemo
        ) &&
        !(config.requiresProviderReviewId && review.providerReviewId === undefined)
    },

    isBulkResponseAllowed(review) {
      const config = self.forProvider(review.contentProvider)

      return config.canRespondBulk
    }

  }

  return self

})
